import React from 'react';
import { Box, Button, Card, CardContent, Grid, Link, Stack, Typography } from '@mui/material';
import {
  CalendarMonth,
  CheckCircle,
  Edit,
  Flag,
  Link as LinkIcon,
  People,
  PlayCircle,
  Schedule,
  Wifi,
  Download
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { useRecordContext } from 'components/record/useRecordContext';
import EventStatusBadge from 'components/admin/EventStatusBadge';
import MapContainer from 'components/common/MapContainer';
import DraggableMarkers from 'components/common/DraggableMarker';
import icons from 'components/Control/Icons/MarkerIcons';
import EditButton from 'components/forms/EditButton';
import EventForm from './EventForm';

const formatCategory = (value?: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '—');

const OverviewCard = ({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.06em' }}>
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </CardContent>
  </Card>
);

const StatusRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Stack direction="row" justifyContent="space-between" sx={{ py: 0.75 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Stack>
);

const EventOverview = () => {
  const { record: event } = useRecordContext();
  const navigate = useNavigate();
  const [elapsed, setElapsed] = React.useState('');

  const subEvents = event.subEvents || [];
  const competitors = event.competitors || [];
  const entries = event.entries || [];
  const gpsSubevents = subEvents.filter((item: { useGPS?: boolean }) => item.useGPS).length;
  const gpsDevices = competitors.filter((item: { device?: { id?: number } }) => item.device?.id).length;
  const publicUrl = `${import.meta.env.VITE_DOMAIN_EVENTS}${event.slug}`;
  const replayUrl = `${import.meta.env.VITE_DOMAIN_REPLAY}${event.slug}`;
  const isLive = event.status === 'live';

  React.useEffect(() => {
    if (!isLive) return undefined;
    const tick = () => {
      const total = Math.max(0, Math.floor(dayjs().diff(dayjs(event.startDate)) / 1000));
      const hours = String(Math.floor(total / 3600)).padStart(2, '0');
      const minutes = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
      const seconds = String(total % 60).padStart(2, '0');
      setElapsed(`${hours}:${minutes}:${seconds}`);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [event.startDate, isLive]);

  const copyPublicLink = async () => {
    await navigator.clipboard.writeText(publicUrl);
    toast.success('Public link copied');
  };

  return (
    <Stack spacing={2.5}>
      {isLive && (
        <Card sx={{ borderColor: '#bbf7d0', backgroundColor: '#f0fdf4' }}>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, mb: 1 }}>
                  LIVE OPERATIONS
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <EventStatusBadge status={event.status} hidden={event.hidden} />
                  <Typography variant="h5">{elapsed || '00:00:00'}</Typography>
                </Stack>
              </Box>
              <Grid container spacing={2} sx={{ width: '100%', maxWidth: { md: 'none', lg: 'none' } }}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    GPS devices
                  </Typography>
                  <Typography variant="h6">{gpsDevices}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Competitors
                  </Typography>
                  <Typography variant="h6">{competitors.length}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Entries
                  </Typography>
                  <Typography variant="h6">{entries.length}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    GPS enabled
                  </Typography>
                  <Typography variant="h6">{gpsSubevents}</Typography>
                </Grid>
              </Grid>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<Wifi />}
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Open Live Tracking
              </Button>
              <Button variant="outlined" onClick={() => navigate(`/my_events/${event.slug}/controls`)} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                Event Controls
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <OverviewCard
            title="Event details"
            action={
              <EditButton label="" variant="text" size="small" color="error">
                <EventForm />
              </EditButton>
            }
          >
            <Typography variant="body1" fontWeight={700}>
              {formatCategory(event.category)}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {dayjs(event.startDate).format('D MMM YYYY')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {dayjs(event.startDate).format('HH:mm')} → {dayjs(event.endDate).format('HH:mm')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Abbreviation
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {event.slug}
            </Typography>
            {event.web && (
              <Link href={event.web} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-block', mt: 1, fontSize: 14 }}>
                {event.name}
              </Link>
            )}
          </OverviewCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <OverviewCard title="Status">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <EventStatusBadge status={event.status} hidden={event.hidden} />
            </Stack>
            <StatusRow label="Public visibility" value={event.hidden ? 'Hidden' : 'Public'} />
            <StatusRow label="Registration" value={event.settings?.canEnter ? 'Open' : 'Closed'} />
            <StatusRow label="GPS tracking" value={gpsSubevents > 0 ? 'Enabled' : 'Off'} />
            <StatusRow label="Is open" value={event.isOpen ? 'Yes' : 'No'} />
            <Box sx={{ mt: 2 }}>
              <EditButton label="Change status" variant="outlined" size="small">
                <EventForm />
              </EditButton>
            </Box>
          </OverviewCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <OverviewCard title="Subevents">
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <CalendarMonth color="secondary" />
              <Typography variant="h5">
                {subEvents.length} {subEvents.length === 1 ? 'Subevent' : 'Subevents'}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {gpsSubevents} GPS enabled
            </Typography>
            {subEvents.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                {subEvents.map((subEvent: { id: number; name: string }) => (
                  <Link
                    key={subEvent.id}
                    component={RouterLink}
                    to={`/sub_events/${subEvent.id}`}
                    sx={{ fontSize: 14, fontWeight: 600 }}
                  >
                    {subEvent.name}
                  </Link>
                ))}
              </Stack>
            )}
            <Button variant="text" sx={{ mt: 2, px: 0 }} onClick={() => navigate(`/my_events/${event.slug}/subevents`)}>
              Manage subevents →
            </Button>
          </OverviewCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <OverviewCard title="Participation">
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <People color="primary" />
              <Typography variant="h5">{competitors.length} Competitors</Typography>
            </Stack>
            <Typography variant="body2">{entries.length} Entries</Typography>
            <Typography variant="body2" color="text.secondary">
              {gpsDevices} GPS devices
            </Typography>
            <Button variant="text" sx={{ mt: 2, px: 0 }} onClick={() => navigate(`/my_events/${event.slug}/entries`)}>
              View entries →
            </Button>
          </OverviewCard>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 4 }}>
          <OverviewCard
            title="Location"
            action={
              <EditButton label="Edit location" variant="text" size="small">
                <EventForm />
              </EditButton>
            }
          >
            <Box sx={{ height: { xs: 220, sm: 280 }, borderRadius: 2, overflow: 'hidden' }}>
              <MapContainer
                center={event.location}
                zoom={event.zoom}
                className="leaflet-crosshair"
                style={{ width: '100%', height: '100%', position: 'relative' }}
              >
                <DraggableMarkers position={event.location} icon={icons['center']} />
              </MapContainer>
            </Box>
          </OverviewCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <OverviewCard title="Event timeline">
            <TimelineRow icon={<CheckCircle color="success" />} label="Event started" value={dayjs(event.startDate).format('D MMM YYYY HH:mm')} />
            <TimelineRow icon={<Flag color="primary" />} label="Published" value={dayjs(event.publicDate).format('D MMM YYYY HH:mm')} />
            <TimelineRow icon={<Schedule color="info" />} label="Event finished" value={dayjs(event.endDate).format('D MMM YYYY HH:mm')} />
          </OverviewCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <OverviewCard title="Quick actions">
            <Grid container spacing={1}>
              <Grid size={6}>
                <QuickAction href={publicUrl} icon={<Wifi />} label="Open Live Tracking" />
              </Grid>
              <Grid size={6}>
                <QuickAction href={replayUrl} icon={<PlayCircle />} label="Open Replay" />
              </Grid>
            </Grid>
            <Button fullWidth variant="outlined" startIcon={<LinkIcon />} sx={{ mt: 1.5 }} onClick={copyPublicLink}>
              Share public link
            </Button>
          </OverviewCard>
        </Grid>
      </Grid>

      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" sx={{ pt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Created {event.createdAt ? dayjs(event.createdAt).format('D MMM YYYY') : ''}
          {event.updatedAt ? ` • Updated ${dayjs(event.updatedAt).format('D MMM YYYY HH:mm')}` : ''}
        </Typography>
      </Stack>
    </Stack>
  );
};

const TimelineRow = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1 }}>
    {icon}
    <Box>
      <Typography variant="body2" fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {value}
      </Typography>
    </Box>
  </Stack>
);

const QuickAction = ({ href, icon, label, internal }: { href: string; icon: React.ReactNode; label: string; internal?: boolean }) => {
  const navigate = useNavigate();
  return (
    <Button
      fullWidth
      variant="outlined"
      onClick={() => (internal ? navigate(href) : window.open(href, '_blank'))}
      sx={{ flexDirection: 'column', py: 2, height: '100%', color: 'text.primary' }}
    >
      <Box sx={{ color: 'primary.main', mb: 0.5 }}>{icon}</Box>
      <Typography variant="caption" fontWeight={600} textAlign="center">
        {label}
      </Typography>
    </Button>
  );
};

export default EventOverview;
